import { useState, useEffect } from 'react';
import { Avatar, Button, Stack, TextField, InputAdornment, IconButton, Link, Grid, Box, 
        Typography, Container, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/Axios';
import i18n from '../translationUtils/i18n';

function Copyright(props) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Copyright © '}
            <Link color="inherit" href="https://firerover.com/">
                Fire Rover LLC.
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const languages = [
    { label: i18n.t('english'), value: 'en' },
    { label: i18n.t('french'), value: 'fr' },
    { label: i18n.t('spanish'), value: 'es' }
  ];

const USER_REGEX = /^[A-z][A-z_]{0,23}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[#?!@$%^&*-/=+><:;"']).{8,26}$/;
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function Register() {
    const [firstName, setFirstName] = useState('');
    const [validFirstName, setFirstValidName] = useState(false);
    const [firstNameFocus, setFirstNameFocus] = useState(false);

    const [lastName, setLastName] = useState('');
    const [validLastName, setLastValidName] = useState(false);
    const [lastNameFocus, setLastNameFocus] = useState(false);

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [phone, setPhone] = useState(null);
    const [validPhone, setValidPhone] = useState(false);
    const [phoneFocus, setPhoneFocus] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [preferredLanguage, setPreferredLanguage] = useState("en");

    const [code, setCode] = useState('');
    const [validCode, setValidCode] = useState(false);
    const [codeFocus, setCodeFocus] = useState(false);

    const [phoneCode, setPhoneCode] = useState('');
    const [validPhoneCode, setValidPhoneCode] = useState(false);
    const [phoneCodeFocus, setPhoneCodeFocus] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState(i18n.t('verificationcodesent'));
    const [dialogText, setDialogText] = useState(i18n.t('checkforverificationcode'));

    const navigate = useNavigate();

    useEffect(() => {
        setFirstValidName(USER_REGEX.test(firstName));
    }, [firstName]);

    useEffect(() => {
        setLastValidName(USER_REGEX.test(lastName));
    }, [lastName]);

    useEffect(() => {
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd);
    }, [pwd, matchPwd]);

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(String(email).toLowerCase()));
    }, [email]);

    useEffect(() => {
        setValidCode(code.length > 0);
    }, [code])

    useEffect(() => {
        setValidPhoneCode(phoneCode.length === 6);
    }, [phoneCode])

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handlePhone = (newValue) => {
        setValidPhone(matchIsValidTel(newValue, {
            onlyCountryies: [], // optional,
            excludedCountryies: [], // optional
            continents: [] // optional
        }));
        setPhone(newValue);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const newUser = {
            email: data.get('email'),
            password: data.get('password'),
            name: data.get('firstName') + ' ' + data.get('lastName'),
            phone: data.get('phone'),
            registerCode: data.get('code'),
        };

        axios.post("/api/User/register", newUser)
            .then((response) => {
                if (newUser.phone?.substring(0, 2) === "+1") {
                    handleSendPhoneVerificationCode();
                } else {
                    handleRegistrationComplete();
                    setOpenDialog(true);
                };
            })
            .catch((error) => {
                console.error(error);
                alert(i18n.t('registerfailed'));
            });
    };

    const handleSendPhoneVerificationCode = () => {
        setOpenDialog(true);

        const newUser = {
            email: email,
            phone: phone,
            prefferedLanguage: preferredLanguage,
        };

        axios.post("/api/User/sendPhoneVerificationCode", newUser)
            .then((response) => {
                alert(i18n.t('verificationcodesent'))
            })
            .catch((error) => {
                console.error(error);
                alert(i18n.t('verificationcodesentfailure'));
            });
    }

    const handleCloseDialog = () => {
        setOpenDialog(false);
        navigate('/signin');
    };

    const handleConfirmDialog = (event) => {
        const newUser = {
            email: email,
            password: pwd,
            name: firstName + ' ' + lastName,
            phone: phone,
            phoneVerifyCode: phoneCode,
        };

        axios.post("/api/User/verifyPhoneCode", newUser)
            .then((response) => {
                handleCodeVerified(newUser);
            })
            .catch((error) => {
                console.error(error);
                handleCodeRejected();
            });
    }

    const handleCodeVerified = (event) => {
        setDialogTitle(i18n.t('codeverified'));
        setDialogText(i18n.t('registrationcomplete'));
    }

    const handleRegistrationComplete = (event) => {
        setDialogTitle(i18n.t('registersuccess'));
        setDialogText(i18n.t('registrationcomplete'));
    }

    const handleCodeRejected = (event) => {
        setDialogTitle(i18n.t('codevalidatefailed'))
    }

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                    <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5">
                    {i18n.t('signup')}
                </Typography>
                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoComplete="given-name"
                                name="firstName"
                                required
                                fullWidth
                                id="firstName"
                                label={i18n.t('firstname')}
                                error={!validFirstName && firstName}
                                helperText={!validFirstName && firstName ? i18n.t("onlyalphabet") : ""}
                                onFocus={() => setFirstNameFocus(true)}
                                onBlur={() => setFirstNameFocus(false)}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                id="lastName"
                                label={i18n.t('lastname')}
                                name="lastName"
                                autoComplete="family-name"
                                error={!validLastName && lastName}
                                helperText={!validLastName && lastName ? i18n.t("onlyalphabet") : ""}
                                onFocus={() => setLastNameFocus(true)}
                                onBlur={() => setLastNameFocus(false)}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                id="email"
                                label={i18n.t("emailaddress")}
                                name="email"
                                autoComplete="email"
                                error={!validEmail && email}
                                helperText={!validEmail && email ? i18n.t("emailisinvalid") : ""}
                                onFocus={() => setEmailFocus(true)}
                                onBlur={() => setEmailFocus(false)}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                id="password"
                                label={i18n.t("password")}
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                error={!validPwd && pwd}
                                helperText={!validPwd && pwd ? i18n.t('passwordrequirementstxt') : ""}
                                onFocus={() => setPwdFocus(true)}
                                onBlur={() => setPwdFocus(false)}
                                onChange={(e) => setPwd(e.target.value)}
                                InputProps={
                                    {
                                        endAdornment: (
                                            < InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                type="password"
                                id="confirmPassword"
                                label={i18n.t('confirmpassword')}
                                name="confirmPassword"
                                autoComplete="confirmPassword"
                                error={!validMatch && matchPwd}
                                helperText={!validMatch && matchPwd ? i18n.t('passwordsdonotmatch') : ""}
                                onFocus={() => setMatchFocus(true)}
                                onBlur={() => setMatchFocus(false)}
                                onChange={(e) => setMatchPwd(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MuiTelInput
                                fullWidth
                                label={i18n.t('phone')}
                                name="phone"
                                value={phone}
                                defaultCountry='US'
                                preferredCountries={['US', 'CA', 'FR']}
                                error={!validPhone && phone}
                                helperText={!validPhone && phone ? "Invalid phone number" : ""}
                                onFocus={() => setPhoneFocus(true)}
                                onBlur={() => setPhoneFocus(false)}
                                onChange={handlePhone} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>{i18n.t('selectpreferredlanguage')}</InputLabel>
                                <Select
                                    value={preferredLanguage}
                                    onChange={(e) => setPreferredLanguage(e.target.value)}
                                    label={i18n.t('selectpreferredlanguage')}
                                    required={true}
                                >
                                    {languages.map((lang) => (
                                    <MenuItem key={lang.value} value={lang.value}>{lang.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                id="code"
                                label={i18n.t('registrationcode')}
                                name="code"
                                autoComplete="code"
                                error={!validCode && codeFocus}
                                helperText={!validCode && codeFocus ? i18n.t('enterregistrationcode') : ""}
                                onFocus={() => setCodeFocus(true)}
                                onBlur={() => setCodeFocus(false)}
                                onChange={(e) => setCode(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={!validPwd || !validMatch || !validFirstName || !validLastName || !validEmail || !validPhone || !validCode ? true : false}
                    >
                        {i18n.t('signup')}
                    </Button>
                    <Grid container justifyContent="flex-end">
                        <Grid item>
                            <Link href="/signin" variant="body2">
                                {i18n.t('alreadyhaveanaccount')} {i18n.t('signin')}
                            </Link>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Copyright sx={{ mt: 5 }} />
            <Dialog open={openDialog}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    {dialogText}
                </DialogContent>
                    {dialogTitle === i18n.t('verificationcodesent') || dialogTitle === i18n.t('verificationcodesentfailure') ? (
                        <>
                            <Stack style={{ paddingLeft: "5%", paddingRight: "5%" }}>
                                <Typography>{i18n.t('accountcreatedcheckforcode')}</Typography>
                            </Stack>
                            <Stack style={{ paddingLeft: "5%", paddingRight: "5%", paddingTop: "5%" }}>
                                <TextField
                                    required
                                    fullWidth
                                    id="phoneCode"
                                    label={i18n.t('phoneverificationcode')}
                                    name="phoneCode"
                                    error={!validPhoneCode && phoneCodeFocus}
                                    helperText={!validPhoneCode && phoneCodeFocus ? i18n.t('enterverificationcode') : ""}
                                    onFocus={() => setPhoneCodeFocus(true)}
                                    onBlur={() => setPhoneCodeFocus(false)}
                                    onChange={(e) => setPhoneCode(e.target.value)}
                                    />
                            </Stack>
                        </>
                    ) : (
                        null
                    )}
                <DialogActions>
                    {dialogTitle === i18n.t('verificationcodesent') || dialogTitle === i18n.t('verificationcodesentfailure') ? (
                        <Button onClick={handleSendPhoneVerificationCode} color="primary">
                            {i18n.t('resendcode')}   
                        </Button>    
                    ) : (
                        null
                    )}
                    {dialogTitle === i18n.t('codeverified') || dialogTitle === i18n.t('registersuccess') ? (
                        <Button onClick={handleCloseDialog} color="primary">
                            {i18n.t('close')}
                        </Button>
                    ) : (
                        <Button onClick={handleConfirmDialog} color="primary">
                            {i18n.t('confirm')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Container>
    );
}